/**
 * Centralized Error Handling Service
 *
 * Provides consistent error handling across the application with:
 * - User-friendly error messages
 * - Error logging and monitoring
 * - Retry mechanisms for transient errors
 * - Error categorization and recovery suggestions
 */

// Error categories for different types of errors
export enum ErrorCategory {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  SERVER = 'server',
  CLIENT = 'client',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown'
}

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// Error action types
export enum ErrorAction {
  RETRY = 'retry',
  REFRESH = 'refresh',
  REAUTHENTICATE = 'reauthenticate',
  CONTACT_SUPPORT = 'contact_support',
  IGNORE = 'ignore'
}

// Standardized error structure
export interface AppError {
  id: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  code: string;
  message: string;
  userMessage: string;
  originalError?: Error | any;
  context?: Record<string, any>;
  timestamp: Date;
  retryable: boolean;
  suggestedAction: ErrorAction;
  recoveryAttempts: number;
  maxRetries: number;
  metadata?: Record<string, any>;
}

// Error handler configuration
export interface ErrorHandlerConfig {
  enableLogging: boolean;
  enableUserNotifications: boolean;
  maxRetries: number;
  retryDelay: number;
  retryBackoffMultiplier: number;
  enableErrorReporting: boolean;
  errorReportingEndpoint?: string;
  logLevel: ErrorSeverity;
}

// Default configuration
const defaultConfig: ErrorHandlerConfig = {
  enableLogging: true,
  enableUserNotifications: true,
  maxRetries: 3,
  retryDelay: 1000,
  retryBackoffMultiplier: 2,
  enableErrorReporting: false,
  logLevel: ErrorSeverity.MEDIUM
};

/**
 * Centralized Error Handler Service
 */
export class ErrorHandler {
  private config: ErrorHandlerConfig;
  private errorHistory: AppError[] = [];
  private errorCallbacks: ((error: AppError) => void)[] = [];

  constructor(config: Partial<ErrorHandlerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
    this.setupGlobalErrorHandlers();
  }

  /**
   * Setup global error handlers for unhandled errors
   */
  private setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = this.handleError(event.reason, {
        context: { type: 'unhandled_promise_rejection' }
      });

      if (this.config.enableLogging) {
        console.error('Unhandled promise rejection:', error);
      }
    });

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      const error = this.handleError(event.error || new Error(event.message), {
        context: {
          type: 'uncaught_error',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });

      if (this.config.enableLogging) {
        console.error('Uncaught error:', error);
      }
    });
  }

  /**
   * Main error handling method
   */
  public handleError(
    error: Error | any,
    context?: { [key: string]: any }
  ): AppError {
    const appError = this.createAppError(error, context);

    // Log error
    if (this.config.enableLogging) {
      this.logError(appError);
    }

    // Store in history
    this.addToHistory(appError);

    // Notify callbacks
    this.notifyCallbacks(appError);

    // Report to external service if enabled
    if (this.config.enableErrorReporting) {
      this.reportError(appError);
    }

    return appError;
  }

  /**
   * Create a standardized AppError from any error
   */
  private createAppError(error: Error | any, context?: Record<string, any>): AppError {
    const category = this.categorizeError(error);
    const severity = this.determineSeverity(error, category);
    const code = this.extractErrorCode(error);
    const message = this.extractErrorMessage(error);
    const userMessage = this.createUserFriendlyMessage(error, category);
    const retryable = this.isRetryable(error, category);
    const suggestedAction = this.suggestAction(error, category, retryable);

    return {
      id: this.generateErrorId(),
      category,
      severity,
      code,
      message,
      userMessage,
      originalError: error,
      context,
      timestamp: new Date(),
      retryable,
      suggestedAction,
      recoveryAttempts: 0,
      maxRetries: this.config.maxRetries,
      metadata: this.extractMetadata(error)
    };
  }

  /**
   * Categorize error based on its properties
   */
  private categorizeError(error: Error | any): ErrorCategory {
    if (!error) return ErrorCategory.UNKNOWN;

    // Network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return ErrorCategory.NETWORK;
    }

    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      return ErrorCategory.NETWORK;
    }

    // Timeout errors
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      return ErrorCategory.TIMEOUT;
    }

    // HTTP status errors
    if (error.status || error.statusCode) {
      const status = error.status || error.statusCode;

      if (status === 401 || status === 403) {
        return ErrorCategory.AUTHENTICATION;
      }

      if (status >= 400 && status < 500) {
        return status === 401 || status === 403
          ? ErrorCategory.AUTHENTICATION
          : ErrorCategory.CLIENT;
      }

      if (status >= 500) {
        return ErrorCategory.SERVER;
      }
    }

    // Jira API specific errors
    if (error.code && typeof error.code === 'string') {
      if (error.code.includes('AUTH') || error.code.includes('PERMISSION')) {
        return ErrorCategory.AUTHORIZATION;
      }

      if (error.code.includes('VALIDATION')) {
        return ErrorCategory.VALIDATION;
      }
    }

    // JavaScript errors
    if (error instanceof Error) {
      if (error.name === 'ReferenceError' || error.name === 'TypeError') {
        return ErrorCategory.CLIENT;
      }
    }

    return ErrorCategory.UNKNOWN;
  }

  /**
   * Determine error severity
   */
  private determineSeverity(error: Error | any, category: ErrorCategory): ErrorSeverity {
    // Critical errors that prevent the app from functioning
    if (category === ErrorCategory.AUTHENTICATION || category === ErrorCategory.AUTHORIZATION) {
      return ErrorSeverity.HIGH;
    }

    // Server errors
    if (category === ErrorCategory.SERVER) {
      return ErrorSeverity.MEDIUM;
    }

    // Network and timeout errors
    if (category === ErrorCategory.NETWORK || category === ErrorCategory.TIMEOUT) {
      return ErrorSeverity.MEDIUM;
    }

    // Client errors are typically lower severity
    if (category === ErrorCategory.CLIENT) {
      return ErrorSeverity.LOW;
    }

    // Default to medium for unknown errors
    return ErrorSeverity.MEDIUM;
  }

  /**
   * Extract error code from error
   */
  private extractErrorCode(error: Error | any): string {
    if (error.code) return String(error.code);
    if (error.status) return `HTTP_${error.status}`;
    if (error.statusCode) return `HTTP_${error.statusCode}`;
    if (error.name) return error.name.toUpperCase();
    return 'UNKNOWN_ERROR';
  }

  /**
   * Extract error message
   */
  private extractErrorMessage(error: Error | any): string {
    if (error instanceof Error) return error.message;
    if (error.message) return error.message;
    if (typeof error === 'string') return error;
    return 'Unknown error occurred';
  }

  /**
   * Create user-friendly error message
   */
  private createUserFriendlyMessage(error: Error | any, category: ErrorCategory): string {
    switch (category) {
      case ErrorCategory.NETWORK:
        return 'Unable to connect. Please check your internet connection and try again.';

      case ErrorCategory.AUTHENTICATION:
        return 'Your session has expired. Please log in again.';

      case ErrorCategory.AUTHORIZATION:
        return 'You don\'t have permission to perform this action.';

      case ErrorCategory.VALIDATION:
        return 'Please check your input and try again.';

      case ErrorCategory.TIMEOUT:
        return 'The request took too long to complete. Please try again.';

      case ErrorCategory.SERVER:
        return 'Something went wrong on our end. Please try again later.';

      case ErrorCategory.CLIENT:
        return 'An unexpected error occurred. Please refresh the page.';

      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Determine if error is retryable
   */
  private isRetryable(error: Error | any, category: ErrorCategory): boolean {
    switch (category) {
      case ErrorCategory.NETWORK:
      case ErrorCategory.TIMEOUT:
      case ErrorCategory.SERVER:
        return true;

      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.AUTHORIZATION:
      case ErrorCategory.VALIDATION:
      case ErrorCategory.CLIENT:
        return false;

      default:
        return false;
    }
  }

  /**
   * Suggest action for error recovery
   */
  private suggestAction(error: Error | any, category: ErrorCategory, retryable: boolean): ErrorAction {
    if (retryable) return ErrorAction.RETRY;

    switch (category) {
      case ErrorCategory.AUTHENTICATION:
        return ErrorAction.REAUTHENTICATE;

      case ErrorCategory.NETWORK:
      case ErrorCategory.SERVER:
        return ErrorAction.REFRESH;

      case ErrorCategory.AUTHORIZATION:
      case ErrorCategory.VALIDATION:
        return ErrorAction.IGNORE;

      default:
        return ErrorAction.CONTACT_SUPPORT;
    }
  }

  /**
   * Extract metadata from error
   */
  private extractMetadata(error: Error | any): Record<string, any> {
    const metadata: Record<string, any> = {};

    if (error.status) metadata.httpStatus = error.status;
    if (error.statusCode) metadata.httpStatus = error.statusCode;
    if (error.url) metadata.url = error.url;
    if (error.method) metadata.method = error.method;
    if (error.stack) metadata.stack = error.stack;
    if (error.timestamp) metadata.originalTimestamp = error.timestamp;

    return metadata;
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log error to console and local storage
   */
  private logError(error: AppError): void {
    const logData = {
      id: error.id,
      category: error.category,
      severity: error.severity,
      code: error.code,
      message: error.message,
      userMessage: error.userMessage,
      timestamp: error.timestamp.toISOString(),
      context: error.context,
      metadata: error.metadata
    };

    // Log to console with appropriate level
    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        console.error('🔴 HIGH SEVERITY ERROR:', logData);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn('🟡 MEDIUM SEVERITY ERROR:', logData);
        break;
      case ErrorSeverity.LOW:
        console.log('🟢 LOW SEVERITY ERROR:', logData);
        break;
    }

    // Store in local storage for debugging
    try {
      const errorLog = JSON.parse(localStorage.getItem('jiraz_error_log') || '[]');
      errorLog.push(logData);

      // Keep only last 50 errors
      if (errorLog.length > 50) {
        errorLog.splice(0, errorLog.length - 50);
      }

      localStorage.setItem('jiraz_error_log', JSON.stringify(errorLog));
    } catch (e) {
      console.warn('Failed to store error in localStorage:', e);
    }
  }

  /**
   * Add error to history
   */
  private addToHistory(error: AppError): void {
    this.errorHistory.unshift(error);

    // Keep only last 100 errors in memory
    if (this.errorHistory.length > 100) {
      this.errorHistory = this.errorHistory.slice(0, 100);
    }
  }

  /**
   * Notify all registered callbacks
   */
  private notifyCallbacks(error: AppError): void {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (e) {
        console.error('Error in error callback:', e);
      }
    });
  }

  /**
   * Report error to external service
   */
  private async reportError(error: AppError): Promise<void> {
    if (!this.config.errorReportingEndpoint) return;

    try {
      const reportData = {
        error: {
          id: error.id,
          category: error.category,
          severity: error.severity,
          code: error.code,
          message: error.message,
          userMessage: error.userMessage,
          timestamp: error.timestamp.toISOString(),
          context: error.context,
          metadata: error.metadata
        },
        userAgent: navigator.userAgent,
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      };

      await fetch(this.config.errorReportingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData)
      });
    } catch (e) {
      console.warn('Failed to report error:', e);
    }
  }

  /**
   * Retry an operation with exponential backoff
   */
  public async retry<T>(
    operation: () => Promise<T>,
    error?: AppError
  ): Promise<T> {
    if (!error || !error.retryable || error.recoveryAttempts >= error.maxRetries) {
      throw error || new Error('Operation cannot be retried');
    }

    const delay = this.config.retryDelay * Math.pow(this.config.retryBackoffMultiplier, error.recoveryAttempts);

    await new Promise(resolve => setTimeout(resolve, delay));

    try {
      return await operation();
    } catch (retryError) {
      error.recoveryAttempts++;
      throw this.handleError(retryError, {
        originalErrorId: error.id,
        retryAttempt: error.recoveryAttempts
      });
    }
  }

  /**
   * Register error callback
   */
  public onError(callback: (error: AppError) => void): () => void {
    this.errorCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.errorCallbacks.indexOf(callback);
      if (index > -1) {
        this.errorCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Get error history
   */
  public getErrorHistory(limit?: number): AppError[] {
    return limit ? this.errorHistory.slice(0, limit) : [...this.errorHistory];
  }

  /**
   * Clear error history
   */
  public clearErrorHistory(): void {
    this.errorHistory = [];
    try {
      localStorage.removeItem('jiraz_error_log');
    } catch (e) {
      console.warn('Failed to clear error log from localStorage:', e);
    }
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Create singleton instance
export const errorHandler = new ErrorHandler();

// Export convenience functions
export const handleError = (error: Error | any, context?: Record<string, any>) =>
  errorHandler.handleError(error, context);

export const retryOperation = <T>(operation: () => Promise<T>, error?: AppError) =>
  errorHandler.retry(operation, error);

export const onError = (callback: (error: AppError) => void) =>
  errorHandler.onError(callback);
